"use client";

import { type MotionStyle, type MotionValue, useMotionValue, useTransform, frame, cancelFrame } from "motion/react";
import { useEffect, useMemo } from "react";
import * as m from "motion/react-m";

interface WordContext {
  text: string;
  isBracketed: boolean;
  isStreaming: boolean;
}

type WordStyle = {
  [prop in keyof React.CSSProperties]:
    | React.CSSProperties[prop]
    | Partial<{ default: React.CSSProperties[prop]; highlighted: React.CSSProperties[prop] }>;
};

export function TextHighlighter({
  mediaRef,
  characters,
  startTimes,
  endTimes,
  withBrackets = true,
  displayCharacters,
  className,
  wordClassName,
  wordStyle,
}: {
  characters: string[];
  className?: string;
  // The characters that are typed into the editor - used to display the full text when streaming response is partially received
  displayCharacters?: string[];
  endTimes: number[];
  mediaRef: React.RefObject<HTMLAudioElement | null>;
  startTimes: number[];
  withBrackets?: boolean;
  wordClassName?: string | ((props: WordContext) => string);
  wordStyle?: WordStyle;
}) {
  const currentTime = useMotionValue(-1);

  // Calculate words and their boundaries
  const words = useMemo(() => {
    const wordList: { endIndex: number; isBracketed?: boolean; startIndex: number; text: string }[] = [];
    let currentStart = 0;
    let currentState: "bracket" | "whitespace" | "word" | null = null;

    // Use displayCharacters if provided and characters array is empty/smaller
    const sourceChars =
      displayCharacters && (!characters.length || characters.length < displayCharacters.length)
        ? displayCharacters
        : characters;

    // Helper function to add current segment to word list
    const addCurrentSegment = (endIndex: number) => {
      if (currentState && currentStart <= endIndex) {
        const text = sourceChars.slice(currentStart, endIndex + 1).join("");
        wordList.push({
          text,
          startIndex: currentStart,
          endIndex,
          isBracketed: currentState === "bracket",
        });
      }
    };

    for (let i = 0; i < sourceChars.length; i++) {
      const char = sourceChars[i];
      const isWhitespace = /\s/.test(char);
      let newState: "bracket" | "whitespace" | "word" | null = null;

      // Determine what state we should be in
      if (char === "[") {
        newState = "bracket";
      } else if (currentState === "bracket" && char !== "]") {
        newState = "bracket"; // Continue bracket until we hit ]
      } else if (currentState === "bracket" && char === "]") {
        newState = "bracket"; // End bracket on ] (this will be the last character of the bracket)
      } else if (isWhitespace) {
        newState = "whitespace";
      } else {
        newState = "word";
      }

      // Handle state transitions
      if (newState !== currentState) {
        // End previous segment
        if (currentState) {
          addCurrentSegment(i - 1);
        }

        // Start new segment
        currentState = newState;
        currentStart = i;
      }

      // Special case: if we just processed a closing bracket, force end the bracket state
      if (currentState === "bracket" && char === "]") {
        addCurrentSegment(i);
        currentState = null;
        currentStart = i + 1;
      }
    }

    // Add the final segment
    if (currentState) {
      addCurrentSegment(sourceChars.length - 1);
    }

    return wordList;
  }, [characters, displayCharacters]);

  // Update current time when audio plays
  useEffect(() => {
    const mediaElement = mediaRef.current;
    if (!mediaElement) return;

    const updateTime = () => {
      currentTime.set(mediaElement.currentTime);
    };

    // Start the animation loop
    frame.render(updateTime, true);

    // Still listen for timeupdate as a fallback and for accuracy
    mediaElement.addEventListener("timeupdate", updateTime);

    return () => {
      cancelFrame(updateTime);
      mediaElement.removeEventListener("timeupdate", updateTime);
    };
  }, [mediaRef, currentTime]);

  return (
    <div className={className} inert>
      {words.map((word, index) => {
        if (word.text.trim() === "") {
          return word.text;
        }

        const isStreaming = Boolean(displayCharacters && (!characters.length || word.startIndex >= characters.length));
        const isBracketed = withBrackets ? (word.isBracketed ?? false) : false;
        const context: WordContext = { text: word.text, isBracketed, isStreaming };
        const className = typeof wordClassName === "function" ? wordClassName(context) : wordClassName;

        return (
          <Word
            key={index}
            word={word}
            currentTime={currentTime}
            startTimes={startTimes}
            endTimes={endTimes}
            className={className}
            wordStyle={wordStyle}
          />
        );
      })}
    </div>
  );
}

function Word({
  word,
  currentTime,
  startTimes,
  endTimes,
  className,
  wordStyle,
}: {
  word: { endIndex: number; isBracketed?: boolean; startIndex: number; text: string };
  currentTime: MotionValue<number>;
  startTimes: number[];
  endTimes: number[];
  className?: string;
  wordStyle?: WordStyle;
}) {
  const varPrefix = "text-highlighter-";
  const highlighted = useTransform(currentTime, time => {
    const isHighlighted = time >= startTimes[word.startIndex] && time <= endTimes[word.endIndex];
    return isHighlighted ? "initial" : " ";
  });
  const def = useTransform(highlighted, highlighted => (highlighted === "initial" ? " " : "initial"));

  const style: React.CSSProperties = Object.fromEntries(
    Object.entries(wordStyle ?? {}).map(([prop, value]) => {
      if (typeof value === "object") {
        const val = Object.entries(value).map(([key, value]) => `var(--${varPrefix}${key}, ${value})`);
        return [prop, val.join(" ")];
      }
      return [prop, value];
    }),
  );

  return (
    <m.span
      className={className}
      style={
        {
          ...style,
          [`--${varPrefix}default`]: def,
          [`--${varPrefix}highlighted`]: highlighted,
        } as MotionStyle
      }
    >
      {word.text}
    </m.span>
  );
}
